import { useEffect, useMemo, useState } from 'react';
import { SITE_URL, getStrings, htmlLang, type Lang } from '../../lib/i18n';
import { plannerStrings, fmt, toolPath, TOOL_SLUGS, type PlannerStrings } from '../../lib/tools';
import {
  DAY_OPTIONS,
  LEVELS,
  addDays,
  buildIcs,
  parsePlannerQuery,
  planCompetition,
  serializePlannerQuery,
  todayIso,
  validate,
  type CompetitionPlan,
  type Days,
  type Level,
  type Phase,
} from '../../lib/competitionPlanner';
import { CopyButton } from './ToolParts';

export const PLANNER_SLUG = TOOL_SLUGS.planner;
const BUILD_DATE = typeof __BUILD_DATE__ === 'string' ? __BUILD_DATE__ : '2026-01-01';
export const EXAMPLE_PLANNER = { weeksOut: 8, days: 3 as Days, level: 'intermediate' as Level };

const PHASE_STYLE: Record<Phase, string> = {
  build: 'bg-[var(--pop-2-soft)] text-[var(--pop-2-edge)]',
  deload: 'bg-elevated text-muted',
  intensification: 'bg-[var(--pop-3-soft)] text-[var(--pop-3-edge)]',
  peak: 'bg-[var(--brand-soft)] text-[var(--brand-ink)]',
  taper: 'bg-[var(--pop-1-soft)] text-[var(--pop-1-edge)]',
};

function fmtDate(iso: string, lang: Lang, opts: Intl.DateTimeFormatOptions) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Intl.DateTimeFormat(htmlLang[lang], { ...opts, timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d)));
}
const short = (iso: string, lang: Lang) => fmtDate(iso, lang, { weekday: 'short', day: 'numeric', month: 'short' });
const long = (iso: string, lang: Lang) => fmtDate(iso, lang, { day: 'numeric', month: 'long', year: 'numeric' });

export function PlannerView({ plan, lang, headingLevel = 'h2' }: { plan: CompetitionPlan; lang: Lang; headingLevel?: 'h2' | 'h3' }) {
  const t = plannerStrings(lang);
  const H = headingLevel;
  const S = headingLevel === 'h2' ? 'h3' : 'h4';
  return (
    <div>
      <p className="text-sm font-bold text-muted mb-4">
        {fmt(t.summary, { daysUntil: plan.daysUntil, weeks: plan.weeks.length, sessions: plan.totalSessions })}
      </p>
      {plan.shortPrep && <p role="status" className="rounded-2xl bg-[var(--pop-4-soft)] text-ink text-sm p-4 mb-4">{t.shortPrep}</p>}

      <div className="card-float p-5 mb-4 break-inside-avoid">
        <H className="text-lg font-black text-ink mb-3">{t.milestonesHeading}</H>
        <ol className="relative border-l-2 border-line ml-2 space-y-3">
          {plan.milestones.map((m, i) => (
            <li key={i} className="pl-4 relative">
              <span className={`absolute -left-[7px] top-1.5 w-3 h-3 rounded-full ${m.kind === 'competition' ? 'bg-[var(--brand)]' : 'bg-line'}`} aria-hidden="true" />
              <p className="text-sm">
                <span className="font-bold text-ink">{short(m.date, lang)}</span>
                <span className="text-muted"> · {t.milestones[m.kind].name}{m.kind === 'competition' ? ' 🏆' : ''}</span>
              </p>
              <p className="text-xs text-faint">{t.milestones[m.kind].body}</p>
            </li>
          ))}
        </ol>
      </div>

      <div className="grid gap-3">
        {plan.weeks.map((w) => (
          <article key={w.index} className="card-chunky p-4 sm:p-5 break-inside-avoid">
            <header className="flex flex-wrap items-center justify-between gap-2">
              <S className="font-black text-ink">
                {fmt(t.week, { n: w.index + 1 })} <span className="text-faint font-bold text-sm">· {fmt(t.weekOf, { date: short(w.monday, lang) })}</span>
              </S>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-black uppercase tracking-wide ${PHASE_STYLE[w.phase]}`}>{t.phases[w.phase].name}</span>
            </header>
            <p className="text-xs text-faint mt-1">{t.phases[w.phase].body}</p>
            {w.sessions.length ? (
              <ul className="mt-2">
                {w.sessions.map((s) => (
                  <li key={s.date} className="flex gap-3 py-2 border-b border-line last:border-b-0">
                    <span className="w-24 flex-shrink-0 text-sm font-bold text-ink">{short(s.date, lang)}</span>
                    <span className="min-w-0">
                      <span className="block text-sm font-bold text-ink">{t.sessions[s.kind].name}</span>
                      <span className="block text-xs text-muted">{t.sessions[s.kind].body}</span>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted mt-2">{t.noSessions}</p>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}

function Segmented<T extends string | number>({ name, legend, value, options, onChange }: { name: string; legend: string; value: T; options: { value: T; label: string }[]; onChange: (v: T) => void }) {
  return (
    <fieldset className="mb-5">
      <legend className="text-sm font-black text-ink mb-2">{legend}</legend>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <label key={String(o.value)} className={`cursor-pointer rounded-full border-2 px-3.5 py-1.5 text-sm font-bold has-[:focus-visible]:ring-4 has-[:focus-visible]:ring-[var(--brand-soft)] ${o.value === value ? 'border-[var(--brand)] bg-[var(--brand)] text-white' : 'border-line bg-surface text-muted hover:border-[var(--brand)] hover:text-ink'}`}>
            <input type="radio" name={name} className="sr-only" checked={o.value === value} onChange={() => onChange(o.value)} />
            {o.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function icsFor(plan: CompetitionPlan, t: PlannerStrings): string {
  const events = plan.weeks.flatMap((w) =>
    w.sessions.map((s) => ({
      date: s.date,
      summary: fmt(t.ics.prefix, { session: t.sessions[s.kind].name }),
      description: `${t.phases[w.phase].name}: ${t.sessions[s.kind].body}`,
    })),
  );
  events.push({ date: plan.inputs.competitionDate, summary: `🏆 ${t.ics.competition}`, description: t.milestones.competition.body });
  return buildIcs({ calendarName: t.ics.calendarName, events, stampDate: plan.inputs.startDate });
}

export function CompetitionPlanner({ lang, embed = false }: { lang: Lang; embed?: boolean }) {
  const t = plannerStrings(lang);
  const c = getStrings(lang).exercisesChrome;
  const [start, setStart] = useState(BUILD_DATE);
  const [date, setDate] = useState(addDays(BUILD_DATE, EXAMPLE_PLANNER.weeksOut * 7));
  const [days, setDays] = useState<Days>(EXAMPLE_PLANNER.days);
  const [level, setLevel] = useState<Level>(EXAMPLE_PLANNER.level);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const today = todayIso();
    setStart(today);
    const q = parsePlannerQuery(new URLSearchParams(window.location.search));
    setDate(q.competitionDate ?? addDays(today, EXAMPLE_PLANNER.weeksOut * 7));
    if (q.days) setDays(q.days);
    if (q.level) setLevel(q.level);
    if (q.competitionDate) setDirty(true);
  }, []);

  const query = serializePlannerQuery({ competitionDate: date, days, level });
  useEffect(() => {
    if (!dirty) return;
    window.history.replaceState(window.history.state, '', `${window.location.pathname}?${query}`);
  }, [query, dirty]);

  const error = validate({ competitionDate: date, startDate: start });
  const plan = useMemo(() => (error ? null : planCompetition({ competitionDate: date, startDate: start, days, level })), [error, date, start, days, level]);

  const download = () => {
    if (!plan) return;
    const blob = new Blob([icsFor(plan, t)], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `armwrestling-competition-${plan.inputs.competitionDate}.ics`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className={`grid gap-6 ${embed ? 'md:grid-cols-[300px_1fr]' : 'lg:grid-cols-[340px_1fr]'} items-start`}>
      <form className="card-float p-5 sm:p-6 lg:sticky lg:top-24 print:hidden" onSubmit={(e) => e.preventDefault()} aria-label={t.form.heading}>
        <h2 className="text-xl font-extrabold text-ink mb-4">{t.form.heading}</h2>
        <label htmlFor="comp-date" className="block text-sm font-black text-ink mb-2">{t.form.date}</label>
        <input
          id="comp-date"
          type="date"
          value={date}
          min={addDays(start, 1)}
          max={addDays(start, 365)}
          onChange={(e) => {
            setDate(e.target.value);
            setDirty(true);
          }}
          aria-invalid={!!error}
          aria-describedby={error ? 'comp-date-error' : 'comp-date-hint'}
          className="field-chunky mb-1"
        />
        {error ? (
          <p id="comp-date-error" role="alert" className="text-sm font-bold text-[var(--brand-ink)] mb-4">{t.errors[error]}</p>
        ) : (
          <p id="comp-date-hint" className="text-xs text-faint mb-4">{fmt(t.form.start, { date: long(start, lang) })}</p>
        )}
        <Segmented name="p-days" legend={t.form.days} value={days} options={DAY_OPTIONS.map((d) => ({ value: d, label: fmt(t.form.daysOption, { n: d }) }))} onChange={(v) => { setDays(v); setDirty(true); }} />
        <Segmented name="p-level" legend={t.form.level} value={level} options={LEVELS.map((l) => ({ value: l, label: c.difficulty[l] }))} onChange={(v) => { setLevel(v); setDirty(true); }} />
      </form>

      <div className="min-w-0">
        <div className="flex flex-wrap gap-2 mb-4 print:hidden">
          <button type="button" onClick={download} disabled={!plan} className="btn-3d btn-3d--sm px-4 py-2">📅 {t.actions.ics}</button>
          <button type="button" onClick={() => window.print()} disabled={!plan} className="btn-3d btn-3d--ghost btn-3d--sm px-4 py-2">🖨️ {t.actions.print}</button>
          <CopyButton getText={() => `${SITE_URL}${toolPath(lang, PLANNER_SLUG)}?${query}`} label={`🔗 ${t.actions.copyLink}`} copiedLabel={t.actions.copied} />
        </div>
        <h2 className="text-2xl md:text-3xl font-extrabold text-ink mb-1">
          {t.milestones.competition.name}: {long(date, lang)}
        </h2>
        {plan && <PlannerView plan={plan} lang={lang} />}
        <div className="mt-4">
          <PlannerSafety lang={lang} />
        </div>
      </div>
    </div>
  );
}

export function PlannerSafety({ lang }: { lang: Lang }) {
  const t = plannerStrings(lang);
  return (
    <section aria-labelledby="planner-safety" className="rounded-3xl border-2 border-[var(--pop-4)] bg-[var(--pop-4-soft)] p-5 sm:p-6 break-inside-avoid">
      <h2 id="planner-safety" className="text-xl font-extrabold text-ink mb-3">⚠️ {t.safety.heading}</h2>
      <ul className="space-y-2 text-sm text-ink/90 list-disc pl-5">
        {t.safety.items.map((s, i) => <li key={i}>{s}</li>)}
      </ul>
    </section>
  );
}

export const exampleCompetitionPlan = () =>
  planCompetition({
    competitionDate: addDays(BUILD_DATE, EXAMPLE_PLANNER.weeksOut * 7),
    startDate: BUILD_DATE,
    days: EXAMPLE_PLANNER.days,
    level: EXAMPLE_PLANNER.level,
  });
